// Shared formatting + filtering helpers for the schedule views (Admin Master
// Schedule and Team Lead Conference Schedule) so both render identically.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Pull yyyy-MM-dd out of a date string (tolerates ISO with a "T" suffix).
export function parseDateParts(dateStr) {
  if (!dateStr) return null;
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], key: `${m[1]}-${m[2]}-${m[3]}` };
}

// Day/date divider rows from the source sheet leak in as "items" whose activity
// is just a date (e.g. "2026-08-31T05:00:00.000Z" or "8/31/2026"). Hide them.
export function isDateHeaderRow(item) {
  const a = String(item.activity || '').trim();
  if (!a) return true;
  if (/^\d{4}-\d{2}-\d{2}([T ]|$)/.test(a)) return true;
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(a)) return true;
  return false;
}

// "Monday, August 31st" from a yyyy-MM-dd date; falls back to the day name.
export function formatDayHeader(dateStr, fallbackDay) {
  const p = parseDateParts(dateStr);
  if (!p) return fallbackDay || 'Unknown';
  const dt = new Date(p.y, p.mo - 1, p.d); // local — avoids UTC off-by-one
  const weekday = WEEKDAYS[dt.getDay()];
  return `${weekday}, ${MONTHS[p.mo - 1]} ${ordinal(p.d)}`;
}

export function formatTime(raw) {
  if (!raw) return '';
  const s = String(raw);
  if (s.includes('T')) {
    const d = new Date(s);
    const h = d.getUTCHours();
    const m = d.getUTCMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  }
  // Handle HH:MM:SS or HH:MM
  if (s.match(/^\d{1,2}:\d{2}/)) {
    const parts = s.split(':');
    const h = parseInt(parts[0]);
    const m = parseInt(parts[1]);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  }
  return s;
}

// Per wiki: All Teams = solid black, Meal = sc-meal (dusty rose), Transport = sc-transport (steel blue)
// hideTeamTags: in the Team Lead view we only want Meal/Transportation pills —
// not "All Teams" or team-name pills (the lead already knows it's their team).
export function buildTags(item, { hideTeamTags = false } = {}) {
  const tags = [];
  const teamStr = (item.team || item.appliesToTeams || '').toString().trim();
  const isMeal = item.isMeal === true || item.isMeal === 'true' || item.isMeal === 'TRUE';
  const isTransport = teamStr.toLowerCase().includes('transportation');
  const isAll = teamStr.toUpperCase().replace(/\s/g, '').includes('ALL');

  if (isMeal) {
    tags.push({ label: 'Meal', cls: 'sc-meal' });
  }

  if (isTransport) {
    tags.push({ label: 'Transportation', cls: 'sc-transport' });
    // If also ALL (shuttle runs), note All Teams too but transport takes visual priority
    if (isAll && !isMeal && !hideTeamTags) {
      tags.push({ label: 'All Teams', cls: '' });
    }
  } else if (isAll) {
    if (!hideTeamTags) tags.push({ label: 'All Teams', cls: '' });
  } else if (!hideTeamTags) {
    const teamNames = teamStr.split(',').map(t => t.trim()).filter(Boolean);
    for (const name of teamNames) {
      tags.push({ label: name, cls: '' });
    }
  }

  return tags;
}

// A "windowed" meal is one Kat has explicitly opened up by setting a meal
// window (backend attaches hasMealWindow + mealWindowStart/End). Until she sets
// one, a meal is fixed-time and just shows its master-schedule time.
export function isWindowedMeal(item) {
  return item.hasMealWindow === true || item.hasMealWindow === 'true' || item.hasMealWindow === 'TRUE';
}

// Is this row a meal at all (regardless of whether a window is set)?
export function isMealItem(item) {
  return item.isMeal === true || item.isMeal === 'true' || item.isMeal === 'TRUE';
}

export function groupByDay(items) {
  const map = {};
  for (const item of items) {
    const p = parseDateParts(item.date);
    // Group by actual date when available, else fall back to the day name.
    const key = p ? p.key : (item.day || 'Unknown');
    if (!map[key]) map[key] = { date: item.date, day: item.day, items: [] };
    map[key].items.push(item);
  }

  const groups = Object.values(map);
  for (const g of groups) {
    // Items carry `start` (master schedule) or `startTime` (person schedule).
    g.items.sort((a, b) => (a.start || a.startTime || '').localeCompare(b.start || b.startTime || ''));
  }

  // Chronological by date; date-less groups sink to the bottom.
  groups.sort((a, b) => {
    const pa = parseDateParts(a.date);
    const pb = parseDateParts(b.date);
    if (pa && pb) return pa.key.localeCompare(pb.key);
    if (pa) return -1;
    if (pb) return 1;
    return 0;
  });

  return groups.map(g => ({ day: formatDayHeader(g.date, g.day), items: g.items }));
}

// SL? flag — tolerant of boolean, 1, or "TRUE"/"Yes"/checkmark text.
export function isSL(item) {
  const v = item.isSL;
  if (v === true || v === 1) return true;
  return ['true', 'yes', 'y', '1', 'x', '✓', '✔'].includes(String(v).trim().toLowerCase());
}

// Does a master-schedule "Team" tag match a team name? Mirrors the backend
// (_teamTagMatches): exact normalized, known alias, or 4+-char prefix either
// direction. "Nicole's team" matches nothing — the admin schedule flags it.
const TEAM_TAG_ALIASES = {
  comms: 'communications',
  comm: 'communications',
  prod: 'production',
  sl: 'sl team',
  sls: 'sl team',
};
export function teamTagMatches(token, teamName) {
  const t = String(token ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  const n = String(teamName ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!t || !n) return false;
  if (t === n) return true;
  const alias = TEAM_TAG_ALIASES[t];
  if (alias && (n === alias || n.startsWith(alias))) return true;
  if (t.length >= 4 && (n.startsWith(t) || t.startsWith(n))) return true;
  return false;
}

// True when an item applies to the given team: either it's an "ALL" item, or
// one of the item's team tags matches the team's name (tolerantly).
export function appliesToTeam(item, teamName) {
  const raw = (item.appliesToTeams || item.team || '').toString();
  if (!raw) return false;
  const tokens = raw.split(',').map(t => t.trim()).filter(Boolean);
  if (tokens.some(t => t.toLowerCase() === 'all')) return true;
  if (teamName && tokens.some(t => teamTagMatches(t, teamName))) return true;
  return false;
}

// Build a person's shareable schedule URL on the CANONICAL domain — copied
// links must always be conference.thebelonging.co, even when the admin is
// browsing on the vercel.app host. (Localhost keeps itself for dev.)
import { publicOrigin } from '../config';

export function personScheduleUrl(personId) {
  return `${publicOrigin()}/s/${personId || 'demo'}`;
}

// Host-only label (no scheme) for display, e.g. "conference.thebelonging.co/s/abc123".
export function personScheduleLabel(personId) {
  return `${publicOrigin().replace(/^https?:\/\//, '')}/s/${personId || 'demo'}`;
}

export function TagPill({ label, cls }) {
  if (!cls) {
    return <span className="sc-pill" style={{ background: 'var(--black)', color: '#fff' }}>{label}</span>;
  }
  return <span className={`sc-pill ${cls}`}>{label}</span>;
}
